import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LearnerState, SavedPlaybookMoment } from '@/types/learner';
import type { SessionRecord } from '@/types/session';
import { SEED_LEARNER } from '@/seed';

/**
 * Learner store — holds the §3 LearnerState shape, persisted to
 * localStorage. On first visit, seeded from src/seed.ts. Every avatar
 * mount should read from here for `startData`, and every session-end
 * writeback should mutate here.
 */

interface LearnerStore {
  state: LearnerState;
  reset: () => void;
  replace: (next: LearnerState) => void;
  /** Add a win moment to the playbook (no-op if the same moment is already saved). */
  savePlaybookMoment: (m: SavedPlaybookMoment) => void;
  /** Remove a saved moment, matched on session_id + timestamp. */
  removePlaybookMoment: (sessionId: string, timestamp: number) => void;
  /**
   * Persist a completed roleplay session. Prepends to session_history,
   * upgrades personas_mastered when the score crosses 85, and is a no-op
   * if a record with the same id already exists.
   */
  completeSession: (session: SessionRecord) => void;
}

export const useLearnerStore = create<LearnerStore>()(
  persist(
    (set) => ({
      state: SEED_LEARNER,
      reset: () => set({ state: SEED_LEARNER }),
      replace: (next) => set({ state: next }),
      savePlaybookMoment: (m) =>
        set((s) => {
          const already = s.state.saved_playbook.some(
            (x) => x.session_id === m.session_id && x.timestamp === m.timestamp,
          );
          if (already) return s;
          return {
            state: {
              ...s.state,
              saved_playbook: [m, ...s.state.saved_playbook],
            },
          };
        }),
      removePlaybookMoment: (sessionId, timestamp) =>
        set((s) => ({
          state: {
            ...s.state,
            saved_playbook: s.state.saved_playbook.filter(
              (x) => !(x.session_id === sessionId && x.timestamp === timestamp),
            ),
          },
        })),
      completeSession: (session) =>
        set((s) => {
          if (s.state.session_history.some((x) => x.id === session.id)) {
            return s;
          }
          const personas_mastered =
            session.score >= 85 && !s.state.personas_mastered.includes(session.persona_id)
              ? [...s.state.personas_mastered, session.persona_id]
              : s.state.personas_mastered;
          return {
            state: {
              ...s.state,
              session_history: [session, ...s.state.session_history],
              personas_mastered,
            },
          };
        }),
    }),
    // Persist key bumped at B5 after seed shape/copy changes so returning
    // visitors pick up the new moments + saved_playbook + transcript.
    // 2026-05-27: bumped to v3 — Comcast-vintage sessions in localStorage
    // had persona_id values that no longer resolve in PERSONAS_SEED after
    // the rename, taking down the dashboard. Fresh seed on next load.
    {
      name: `sales-academy-learner-v3-${import.meta.env.BASE_URL.replace(/\//g, '') || 'root'}`,
    },
  ),
);

/** Convenience selector for components that only need the data. */
export const useLearner = () => useLearnerStore((s) => s.state);
